// src/utils/dashboardViewState.js
//
// Session-scoped persistence keys for the clinician dashboard's *view
// configuration* (search/filter values, the active quick-filter chip, the
// current/archived tab, and each table's sort order), plus a helper to clear
// them on sign-out.
//
// The actual persistence is done by the generic useSessionState hook
// (src/hooks/useSessionState.js) — this module only owns the dashboard-specific
// key namespace and the cross-user clearing, mirroring the codebase convention
// of centralising storage keys (see src/pages/wlc/constants/storageKeys.js).
//
// WHY THIS EXISTS
// ----------------
// The dashboard and its patient tables are route components, so every time a
// clinician opens a patient and navigates back, those components unmount and
// their local state resets to defaults — wiping the filter they had just typed.
// Clinicians reported having to re-type a patient's name several times when
// bouncing between records. Persisting the view config for the browser-tab
// session keeps the tables exactly as they left them.
//
// IMPORTANT — this only persists VIEW CONFIG, never patient data. The patient
// list itself (including optimistic archive/assign/add updates) is owned by
// GlobalStateContext, which already survives navigation and is cached
// separately. Each table is a pure derivation of (patientsData → filter →
// sort), so restoring the config simply re-applies it to the up-to-date list.
//
// CROSS-USER SAFETY
// -----------------
// Filters can contain a patient's name (mild PII), so the whole namespace is
// cleared on sign-out / de-auth via clearDashboardViewState(), wired into the
// same GlobalStateContext branch that clears patientsData. That branch also
// runs on token expiry, so a different clinician signing in on a shared machine
// never inherits the previous one's filters.

// Bump the version segment if any persisted shape changes incompatibly, so
// stale entries from older builds are simply ignored rather than mis-read.
const PREFIX = 'td.dash.v1.';

export const DASHBOARD_KEYS = {
  tab: PREFIX + 'tab',
  chip: PREFIX + 'chip',
  currentFilters: PREFIX + 'currentFilters',
  archivedFilters: PREFIX + 'archivedFilters',
  sort: PREFIX + 'sort',
  archivedSort: PREFIX + 'archivedSort',
  providerAccountsSort: PREFIX + 'providerAccountsSort',
  // Staff (provider accounts) search/filter values: name/email substrings and a
  // date-added range. Like the patient filters these can contain a staff
  // member's name (mild PII), so they are wiped on sign-out by the loop below.
  staffFilters: PREFIX + 'staffFilters',
  // Recently-viewed patient list: up to 5 entries with patientId, name, module,
  // and viewedAt timestamp. Contains patient names (mild PII), so it is wiped
  // on sign-out by the same clearDashboardViewState() loop as filters above.
  recentlyViewed: PREFIX + 'recentlyViewed',
};

/**
 * Remove every persisted dashboard view-config entry. Called on sign-out /
 * de-auth so the next user on the same browser tab starts from a clean default
 * view and never sees the previous clinician's filters.
 */
export function clearDashboardViewState() {
  try {
    Object.values(DASHBOARD_KEYS).forEach((k) => sessionStorage.removeItem(k));
  } catch (_) {
    // sessionStorage unavailable — nothing to clear.
  }
}
